/**
 * Capacitor Live Update (OTA) 서비스
 *
 * 네이티브 앱에서 웹 번들을 OTA로 업데이트하는 기능을 제공한다.
 * CloudFront에 배포된 latest.json을 조회하여 새 버전이 있으면
 * zip을 다운로드하고 다음 실행 시 적용한다.
 *
 * PWA 환경에서는 동작하지 않으며, pwaUpdate.ts가 대신 처리한다.
 */
import { Capacitor } from "@capacitor/core";
import { LiveUpdate } from "@capawesome/capacitor-live-update";
import { createLogger } from "@ict/shared/logger";

const logger = createLogger({ module: "LiveUpdate" });

/** CloudFront latest.json 응답 구조 */
interface LatestBundle {
  version: string;
  url: string;
  checksum: string;
}

/**
 * 환경별 CloudFront base URL 반환
 * VITE_APP_ENV: stage | prod (기본값: stage)
 */
function getBaseUrl(): string {
  const env = import.meta.env.VITE_APP_ENV === "prod" ? "prod" : "stage";
  return `https://media2.koreadcc.com/live-update/${env}`;
}

/**
 * 현재 번들이 정상임을 플러그인에 알린다.
 * readyTimeout 내에 호출하지 않으면 기본 번들로 롤백된다.
 */
export async function ready(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const result = await LiveUpdate.ready();
    if (result.rollback) {
      logger.warn("이전 번들에서 롤백 발생", {
        previousBundleId: result.previousBundleId,
        currentBundleId: result.currentBundleId,
      });
    } else {
      logger.debug("번들 준비 완료", {
        currentBundleId: result.currentBundleId,
      });
    }
  } catch (error) {
    logger.error("ready() 호출 실패", error);
  }
}

/**
 * 업데이트 체크 및 적용
 *
 * 1. CloudFront에서 latest.json 조회
 * 2. 현재 번들과 버전 비교
 * 3. 새 버전이면 zip 다운로드 → setNextBundle → reload
 *
 * 네트워크 실패 시 조용히 실패하며, 앱 사용에 영향을 주지 않는다.
 */
export async function checkForUpdate(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const baseUrl = getBaseUrl();
    const response = await fetch(`${baseUrl}/latest.json`, {
      cache: "no-cache",
    });

    if (!response.ok) {
      logger.debug("latest.json 조회 실패", { status: response.status });
      return;
    }

    const latest: LatestBundle = await response.json();
    if (!latest.version || !latest.url || !latest.checksum) {
      logger.debug("latest.json 형식 오류");
      return;
    }

    // 현재 번들 버전과 비교
    const { bundleId: currentBundleId } = await LiveUpdate.getCurrentBundle();
    if (currentBundleId === latest.version) {
      logger.debug("최신 버전 사용 중", { version: latest.version });
      return;
    }

    // 이미 다운로드된 번들인지 확인
    const { bundleIds } = await LiveUpdate.getDownloadedBundles();
    if (bundleIds.includes(latest.version)) {
      logger.info("이미 다운로드된 번들 적용", { version: latest.version });
      await LiveUpdate.setNextBundle({ bundleId: latest.version });
      await LiveUpdate.reload();
      return;
    }

    // 새 번들 다운로드
    logger.info("새 번들 다운로드 시작", { version: latest.version });
    await LiveUpdate.downloadBundle({
      bundleId: latest.version,
      url: latest.url,
      checksum: latest.checksum,
    });

    // 다음 번들로 설정 후 reload
    await LiveUpdate.setNextBundle({ bundleId: latest.version });
    logger.info("새 번들 적용 - 앱 reload", { version: latest.version });
    await LiveUpdate.reload();
  } catch (error) {
    // 네트워크 실패 등 - 조용히 실패 (앱 사용에 영향 없음)
    logger.warn("업데이트 체크 실패", error);
  }
}
